import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, RefreshCw, Save, Plus, Trash2, Eye, EyeOff,
  Sparkles, CreditCard, Phone, FileText, Copy, Check, Link as LinkIcon, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { WelcomeLinkPanel } from "@/components/admin/WelcomeLinkPanel";
import { ClientAiSettingsPanel } from "@/components/admin/ClientAiSettingsPanel";
import { Video, Bot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/* SERVICES                                                                   */
/* -------------------------------------------------------------------------- */
const blankService = () => ({
  id: "",
  name_es: "",
  name_en: "",
  category: "lectura",
  price_mxn: 0,
  price_usd: 0,
  description: "",
  hidden: false,
  order: 0,
});

const ServicesPanel = () => {
  const { authHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(blankService());
  const [editing, setEditing] = useState(null); // id being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/admin/config/services`, { headers: authHeader() }),
        axios.get(`${API}/admin/config/categories`, { headers: authHeader() }).catch(() => ({ data: [] })),
      ]);
      setItems(r.data || []);
      setCategories((c.data || []).filter((x) => !x.internal));
    } catch {
      toast.error("No se pudieron cargar los servicios");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const categoryOptions = categories.length
    ? categories.map((c) => ({ id: c.id, label: c.name }))
    : [{ id: "lectura", label: "Lectura" }, { id: "hechiceria", label: "Hechicería" }, { id: "pacto", label: "Pacto" }, { id: "otro", label: "Otro" }];

  useEffect(() => {
    load();
  }, [load]);

  const saveNew = async () => {
    if (!draft.name_es.trim()) {
      toast.error("Nombre en español es obligatorio");
      return;
    }
    try {
      await axios.post(`${API}/admin/config/services`, draft, { headers: authHeader() });
      toast.success("Servicio creado");
      setDraft(blankService());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    }
  };

  const saveEdit = async (svc) => {
    try {
      await axios.put(`${API}/admin/config/services/${svc.id}`, svc, { headers: authHeader() });
      toast.success("Servicio actualizado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este servicio permanentemente?")) return;
    try {
      await axios.delete(`${API}/admin/config/services/${id}`, { headers: authHeader() });
      toast.success("Eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-4" data-testid="config-services">
      {/* Create form */}
      <div className="glass-dark rounded-2xl p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Plus className="w-4 h-4 text-[#d4af37]" />
          <span className="text-white font-serif text-sm">Nuevo servicio</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input data-testid="svc-name-es" placeholder="Nombre (ES)" value={draft.name_es} onChange={(e) => setDraft({ ...draft, name_es: e.target.value })} className="bg-black/40 border-white/10 text-white" />
          <Input data-testid="svc-name-en" placeholder="Nombre (EN)" value={draft.name_en} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} className="bg-black/40 border-white/10 text-white" />
          <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
            <SelectTrigger data-testid="svc-category" className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
              {categoryOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="svc-price-mxn" type="number" min={0} placeholder="Precio MXN" value={draft.price_mxn} onChange={(e) => setDraft({ ...draft, price_mxn: Number(e.target.value) })} className="bg-black/40 border-white/10 text-white" />
          <Input data-testid="svc-price-usd" type="number" min={0} placeholder="Precio USD" value={draft.price_usd} onChange={(e) => setDraft({ ...draft, price_usd: Number(e.target.value) })} className="bg-black/40 border-white/10 text-white" />
          <Input data-testid="svc-order" type="number" placeholder="Orden" value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} className="bg-black/40 border-white/10 text-white" />
        </div>
        <Textarea data-testid="svc-description" rows={2} placeholder="Descripción (opcional)" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="bg-black/40 border-white/10 text-white mt-2" />
        <button type="button" data-testid="svc-create" onClick={saveNew} className="btn-gold mt-3 px-5 py-2 rounded-full text-xs uppercase tracking-widest">Crear servicio</button>
      </div>

      {/* List */}
      <div className="glass-dark rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500 italic">Sin servicios.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((s) =>
              editing === s.id ? (
                <div key={s.id} className="p-4 bg-black/30">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input value={s.name_es} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, name_es: e.target.value } : x))} className="bg-black/40 border-white/10 text-white" placeholder="Nombre ES" />
                    <Input value={s.name_en || ""} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, name_en: e.target.value } : x))} className="bg-black/40 border-white/10 text-white" placeholder="Nombre EN" />
                    <Select value={s.category || "lectura"} onValueChange={(v) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, category: v } : x))}>
                      <SelectTrigger className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
                        {categoryOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="number" value={s.price_mxn} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, price_mxn: Number(e.target.value) } : x))} className="bg-black/40 border-white/10 text-white" placeholder="MXN" />
                    <Input type="number" value={s.price_usd} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, price_usd: Number(e.target.value) } : x))} className="bg-black/40 border-white/10 text-white" placeholder="USD" />
                    <Input type="number" value={s.order || 0} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, order: Number(e.target.value) } : x))} className="bg-black/40 border-white/10 text-white" placeholder="Orden" />
                  </div>
                  <Textarea rows={2} value={s.description || ""} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, description: e.target.value } : x))} className="bg-black/40 border-white/10 text-white mt-2" placeholder="Descripción" />
                  <label className="inline-flex items-center gap-2 text-xs text-neutral-300 mt-2">
                    <input type="checkbox" checked={!!s.hidden} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, hidden: e.target.checked } : x))} />
                    Oculto en la web pública
                  </label>
                  <div className="flex gap-2 mt-3">
                    <button type="button" onClick={() => saveEdit(s)} className="btn-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest"><Save className="w-3.5 h-3.5 inline mr-1" />Guardar</button>
                    <button type="button" onClick={() => { setEditing(null); load(); }} className="px-4 py-2 rounded-full border border-white/10 text-neutral-300 text-xs uppercase tracking-widest">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div key={s.id} className="p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-white font-medium flex items-center gap-2">
                      {s.name_es}
                      {s.hidden && <span className="text-[10px] uppercase bg-amber-500/15 text-amber-300 px-2 py-0.5 rounded-full">Oculto</span>}
                    </div>
                    <div className="text-[11px] text-neutral-500 font-mono">{s.id}</div>
                    {s.description && <div className="text-xs text-neutral-400 mt-1">{s.description}</div>}
                  </div>
                  <div className="text-xs text-neutral-300 text-right">
                    <div>${Number(s.price_mxn).toLocaleString("es-MX")} MXN</div>
                    <div>${Number(s.price_usd).toLocaleString("es-MX")} USD</div>
                    <div className="text-[10px] text-neutral-500 uppercase">{s.category}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button type="button" data-testid={`svc-edit-${s.id}`} onClick={() => setEditing(s.id)} className="text-[10px] uppercase tracking-widest text-[#d4af37] hover:text-white border border-[#d4af37]/30 rounded-md px-2 py-1">Editar</button>
                    <button type="button" data-testid={`svc-del-${s.id}`} onClick={() => remove(s.id)} className="text-[10px] uppercase tracking-widest text-red-300 hover:text-white border border-red-500/30 rounded-md px-2 py-1">Eliminar</button>
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* PAYMENT METHODS                                                            */
/* -------------------------------------------------------------------------- */
const blankPayment = () => ({ id: "", label: "", instructions: "", currency: "MXN", hidden: false, order: 0 });

const PaymentMethodsPanel = () => {
  const { authHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(blankPayment());
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/config/payment-methods`, { headers: authHeader() });
      setItems(r.data || []);
    } catch {
      toast.error("No se pudieron cargar los métodos de pago");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.label.trim() || !draft.instructions.trim()) {
      toast.error("Etiqueta e instrucciones son obligatorias");
      return;
    }
    try {
      await axios.post(`${API}/admin/config/payment-methods`, draft, { headers: authHeader() });
      toast.success("Método creado");
      setDraft(blankPayment());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    }
  };

  const save = async (m) => {
    try {
      await axios.put(`${API}/admin/config/payment-methods/${m.id}`, m, { headers: authHeader() });
      toast.success("Actualizado");
      setEditing(null);
      load();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este método de pago?")) return;
    try {
      await axios.delete(`${API}/admin/config/payment-methods/${id}`, { headers: authHeader() });
      toast.success("Eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-4" data-testid="config-payments">
      <div className="glass-dark rounded-2xl p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Plus className="w-4 h-4 text-[#d4af37]" />
          <span className="text-white font-serif text-sm">Nuevo método de pago</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input data-testid="pm-label" placeholder="Nombre visible (ej. PayPal)" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="bg-black/40 border-white/10 text-white" />
          <Input data-testid="pm-currency" placeholder="Moneda (MXN/USD)" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} className="bg-black/40 border-white/10 text-white" />
          <Input data-testid="pm-order" type="number" placeholder="Orden" value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} className="bg-black/40 border-white/10 text-white" />
        </div>
        <Textarea data-testid="pm-instructions" rows={2} placeholder="Instrucciones que verá el cliente (CLABE, link de PayPal, etc.)" value={draft.instructions} onChange={(e) => setDraft({ ...draft, instructions: e.target.value })} className="bg-black/40 border-white/10 text-white mt-2" />
        <button type="button" data-testid="pm-create" onClick={create} className="btn-gold mt-3 px-5 py-2 rounded-full text-xs uppercase tracking-widest">Crear método</button>
      </div>

      <div className="glass-dark rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500 italic">Sin métodos de pago.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((m) =>
              editing === m.id ? (
                <div key={m.id} className="p-4 bg-black/30 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input value={m.label} onChange={(e) => setItems((xs) => xs.map((x) => x.id === m.id ? { ...x, label: e.target.value } : x))} className="bg-black/40 border-white/10 text-white" placeholder="Etiqueta" />
                    <Input value={m.currency || ""} onChange={(e) => setItems((xs) => xs.map((x) => x.id === m.id ? { ...x, currency: e.target.value } : x))} className="bg-black/40 border-white/10 text-white" placeholder="Moneda" />
                    <Input type="number" value={m.order || 0} onChange={(e) => setItems((xs) => xs.map((x) => x.id === m.id ? { ...x, order: Number(e.target.value) } : x))} className="bg-black/40 border-white/10 text-white" placeholder="Orden" />
                  </div>
                  <Textarea rows={3} value={m.instructions} onChange={(e) => setItems((xs) => xs.map((x) => x.id === m.id ? { ...x, instructions: e.target.value } : x))} className="bg-black/40 border-white/10 text-white" placeholder="Instrucciones" />
                  <label className="inline-flex items-center gap-2 text-xs text-neutral-300">
                    <input type="checkbox" checked={!!m.hidden} onChange={(e) => setItems((xs) => xs.map((x) => x.id === m.id ? { ...x, hidden: e.target.checked } : x))} />
                    Oculto al cliente
                  </label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => save(m)} className="btn-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest"><Save className="w-3.5 h-3.5 inline mr-1" />Guardar</button>
                    <button type="button" onClick={() => { setEditing(null); load(); }} className="px-4 py-2 rounded-full border border-white/10 text-neutral-300 text-xs uppercase tracking-widest">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div key={m.id} className="p-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-white font-medium flex items-center gap-2">
                        {m.label}
                        <span className="text-[10px] uppercase bg-[#d4af37]/15 text-[#d4af37] px-2 py-0.5 rounded-full">{m.currency}</span>
                        {m.hidden && <span className="text-[10px] uppercase bg-amber-500/15 text-amber-300 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><EyeOff className="w-3 h-3" />Oculto</span>}
                      </div>
                      <div className="text-[11px] text-neutral-500 font-mono">{m.id}</div>
                      <p className="text-xs text-neutral-300 mt-1 whitespace-pre-wrap">{m.instructions}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button type="button" data-testid={`pm-edit-${m.id}`} onClick={() => setEditing(m.id)} className="text-[10px] uppercase tracking-widest text-[#d4af37] hover:text-white border border-[#d4af37]/30 rounded-md px-2 py-1">Editar</button>
                      <button type="button" data-testid={`pm-del-${m.id}`} onClick={() => remove(m.id)} className="text-[10px] uppercase tracking-widest text-red-300 hover:text-white border border-red-500/30 rounded-md px-2 py-1">Eliminar</button>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* CONTACT INFO                                                               */
/* -------------------------------------------------------------------------- */
const ContactPanel = () => {
  const { authHeader } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/config/contact`, { headers: authHeader() });
      setForm(r.data || {});
    } catch {
      toast.error("No se pudo cargar la información de contacto");
      setForm({});
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config/contact`, form, { headers: authHeader() });
      toast.success("Contacto actualizado");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>;
  }

  const F = (label, key, ph = "") => (
    <div>
      <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">{label}</Label>
      <Input
        data-testid={`contact-${key}`}
        value={form[key] || ""}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={ph}
        className="bg-black/40 border-white/10 text-white"
      />
    </div>
  );

  return (
    <div className="glass-dark rounded-2xl p-4 sm:p-5 space-y-3" data-testid="config-contact">
      <div className="flex items-center gap-2 mb-2">
        <Phone className="w-4 h-4 text-[#d4af37]" />
        <span className="font-serif text-base text-white">Información de contacto</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F("WhatsApp", "whatsapp", "+52 662 287 0805")}
        {F("Email", "email", "crisangelastral@gmail.com")}
        {F("Teléfono", "phone")}
        {F("Dirección", "address")}
        {F("Instagram", "instagram", "@usuario")}
        {F("Facebook", "facebook")}
        {F("TikTok", "tiktok")}
        {F("YouTube", "youtube")}
      </div>
      <button type="button" data-testid="contact-save" onClick={save} disabled={saving} className="btn-gold mt-2 px-5 py-2 rounded-full text-xs uppercase tracking-widest disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <><Save className="w-3.5 h-3.5 inline mr-1" />Guardar</>}
      </button>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* PAGE TEXTS                                                                 */
/* -------------------------------------------------------------------------- */
const PagesPanel = () => {
  const { authHeader } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/config/pages`, { headers: authHeader() });
      setForm(r.data || {});
    } catch {
      toast.error("No se pudieron cargar los textos del sitio");
      setForm({});
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config/pages`, form, { headers: authHeader() });
      toast.success("Textos actualizados");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>;

  return (
    <div className="glass-dark rounded-2xl p-4 sm:p-5 space-y-3" data-testid="config-pages">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4 text-[#d4af37]" />
        <span className="font-serif text-base text-white">Textos del sitio</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Título hero</Label>
          <Input data-testid="pg-hero-title" value={form.hero_title || ""} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} className="bg-black/40 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Subtítulo hero</Label>
          <Input data-testid="pg-hero-sub" value={form.hero_subtitle || ""} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} className="bg-black/40 border-white/10 text-white" />
        </div>
      </div>
      <div>
        <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Texto "Sobre mí"</Label>
        <Textarea data-testid="pg-about" rows={3} value={form.about_text || ""} onChange={(e) => setForm({ ...form, about_text: e.target.value })} className="bg-black/40 border-white/10 text-white" />
      </div>
      <div>
        <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Pie de página</Label>
        <Input data-testid="pg-footer" value={form.footer_text || ""} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} className="bg-black/40 border-white/10 text-white" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">SEO descripción</Label>
          <Input data-testid="pg-seo-desc" value={form.seo_description || ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="bg-black/40 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">SEO keywords</Label>
          <Input data-testid="pg-seo-kw" value={form.seo_keywords || ""} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })} className="bg-black/40 border-white/10 text-white" />
        </div>
      </div>
      <button type="button" data-testid="pages-save" onClick={save} disabled={saving} className="btn-gold px-5 py-2 rounded-full text-xs uppercase tracking-widest disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <><Save className="w-3.5 h-3.5 inline mr-1" />Guardar</>}
      </button>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* MAIN COMPONENT — TABBED                                                    */
/* -------------------------------------------------------------------------- */
const SECTIONS = [
  { id: "welcome",  label: "Enlace de bienvenida", icon: Video, render: () => <WelcomeLinkPanel /> },
  { id: "client_ai", label: "IA del chat", icon: Bot, render: () => <ClientAiSettingsPanel /> },
  { id: "services", label: "Servicios", icon: Sparkles, render: () => <ServicesPanel /> },
  { id: "payments", label: "Pagos", icon: CreditCard, render: () => <PaymentMethodsPanel /> },
  { id: "contact",  label: "Contacto", icon: Phone, render: () => <ContactPanel /> },
  { id: "pages",    label: "Textos del sitio", icon: FileText, render: () => <PagesPanel /> },
];

export const AdminConfigSection = () => {
  const [tab, setTab] = useState("services");
  const [linkCopied, setLinkCopied] = useState(false);
  const Active = SECTIONS.find((s) => s.id === tab);
  const catalogUrl = (typeof window !== "undefined" ? window.location.origin : "") + "/servicios";
  const copyCatalog = async () => {
    try {
      await navigator.clipboard.writeText(catalogUrl);
      setLinkCopied(true);
      toast.success("Link del catálogo copiado");
      setTimeout(() => setLinkCopied(false), 1800);
    } catch {
      toast.error("No pudimos copiar. Copia manualmente.");
    }
  };
  return (
    <div className="space-y-4" data-testid="admin-config-section">
      {/* Public catalog shareable link */}
      <div
        data-testid="admin-catalog-link-card"
        className="rounded-2xl border border-[#d4af37]/30 bg-gradient-to-r from-[#d4af37]/[0.06] to-transparent p-4 flex flex-wrap items-center gap-3"
      >
        <div className="w-11 h-11 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center shrink-0">
          <LinkIcon className="w-5 h-5 text-[#d4af37]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-white text-sm font-semibold">Catálogo público para compartir</div>
          <div className="text-[11px] text-neutral-400 mb-1">
            Comparte este link con clientes para que vean todos tus servicios, precios y métodos de pago (no requiere iniciar sesión).
          </div>
          <div className="text-xs font-mono text-[#d4af37] break-all">{catalogUrl}</div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            data-testid="admin-catalog-copy"
            onClick={copyCatalog}
            className={`px-3 py-2 rounded-full text-[11px] uppercase tracking-widest border flex items-center gap-1 ${linkCopied ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10"}`}
          >
            {linkCopied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar link</>}
          </button>
          <a
            data-testid="admin-catalog-open"
            href="/servicios"
            target="_blank"
            rel="noreferrer"
            className="px-3 py-2 rounded-full text-[11px] uppercase tracking-widest border border-white/10 text-neutral-300 hover:text-white hover:border-[#d4af37]/40 flex items-center gap-1"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Abrir
          </a>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = s.id === tab;
          return (
            <button
              key={s.id}
              type="button"
              data-testid={`config-tab-${s.id}`}
              onClick={() => setTab(s.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-all ${
                active
                  ? "bg-[#d4af37] text-black"
                  : "border border-white/10 text-neutral-300 hover:text-white hover:border-[#d4af37]/40"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          );
        })}
      </div>
      {Active?.render()}
    </div>
  );
};

export default AdminConfigSection;
